import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, Icon, StatusBadge, DueBadge } from '@carrent/ui'
import {
  useTaskCalendar,
  useVideoCalendar,
  dayKey,
  WEEKDAY_LABELS,
  MONTH_LABELS,
  describeDue,
  priorityMeta,
  startOfDay,
} from '@carrent/domain'

/**
 * Календарь задач.
 *
 * Десктопная композиция: сетка месяца слева, день — справа.
 * Сетка отвечает на вопрос «где завал», панель дня — «что делать
 * сегодня». Разложить одно в другое не выйдет: в ячейку месяца
 * помещается три строки, а в день их бывает пятнадцать.
 *
 * По умолчанию открывается сегодняшний день — именно за этим сюда
 * и приходят. Прошедшие дни показываются приглушённо, но остаются
 * кликабельными: просроченное никуда не делось.
 */

const MAX_CHIPS_IN_CELL = 3

function DayCell({ date, tasks, calls = [], isToday, isCurrentMonth, isSelected, onSelect }) {
  const overdue = tasks.filter((t) => describeDue(t)?.isOverdue).length
  const visible = tasks.slice(0, MAX_CHIPS_IN_CELL)
  const rest = tasks.length - visible.length

  const classes = [
    'cal-cell',
    isCurrentMonth ? '' : 'cal-cell--muted',
    isToday ? 'cal-cell--today' : '',
    isSelected ? 'cal-cell--selected' : '',
    tasks.length || calls.length ? '' : 'cal-cell--empty',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      className={classes}
      onClick={() => onSelect(date)}
      aria-pressed={isSelected}
      aria-label={`${date.getDate()} ${MONTH_LABELS[date.getMonth()]}, задач: ${tasks.length}, звонков: ${calls.length}`}
    >
      <span className="cal-cell__head">
        <span className="cal-cell__num">{date.getDate()}</span>
        {overdue > 0 && <span className="cal-cell__dot" title="Есть просроченные" />}
        {tasks.length > 0 && <span className="cal-cell__count">{tasks.length}</span>}
      </span>

      <span className="cal-cell__list">
        {visible.map((t) => (
          <span
            key={t.id}
            className={`cal-chip cal-chip--${priorityMeta(t.priority).tone}`}
            title={t.title}
          >
            {t.title}
          </span>
        ))}
        {rest > 0 && <span className="cal-cell__more">ещё {rest}</span>}
        {calls.slice(0, 2).map((c) => (
          <span key={c.id} className="cal-cell__call" title={`Видеозвонок: ${c.title}`}>
            📹 {c.title}
          </span>
        ))}
      </span>
    </button>
  )
}

export default function AdminCalendarPage() {
  const navigate = useNavigate()

  const today = useMemo(() => startOfDay(new Date()), [])
  const [anchor, setAnchor] = useState(() => new Date())
  const [selected, setSelected] = useState(today)
  const [scope, setScope] = useState('all')
  const [openOnly, setOpenOnly] = useState(true)

  const { weeks, tasksOn, total, loading, error, reload } = useTaskCalendar({
    anchor,
    scope,
    openOnly,
  })

  // Диапазон видимой сетки (6 недель) — как считает useTaskCalendar.
  const calRange = useMemo(() => {
    const first = weeks[0][0]
    const last = weeks[weeks.length - 1][6]
    const to = new Date(last)
    to.setHours(23, 59, 59, 999)
    return { from: first.toISOString(), to: to.toISOString() }
  }, [weeks])

  const { callsOn } = useVideoCalendar({ from: calRange.from, to: calRange.to })

  const shiftMonth = (delta) => {
    const next = new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1)
    setAnchor(next)
  }

  const goToday = () => {
    setAnchor(new Date())
    setSelected(today)
  }

  const selectedTasks = tasksOn(selected)
  const monthTitle = `${MONTH_LABELS[anchor.getMonth()]} ${anchor.getFullYear()}`

  return (
    <div className="flex-col gap">
      <PageHeader
        title="Календарь задач"
        right={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => navigate('/admin/tasks/new')}
          >
            <Icon name="plus" size={14} />
            Создать
          </button>
        }
      />

      <div className="toolbar">
        <div className="cal-nav">
          <button
            type="button"
            className="btn-icon"
            onClick={() => shiftMonth(-1)}
            aria-label="Предыдущий месяц"
          >
            <Icon name="chevronLeft" size={18} />
          </button>
          <span className="cal-nav__title">{monthTitle}</span>
          <button
            type="button"
            className="btn-icon"
            onClick={() => shiftMonth(1)}
            aria-label="Следующий месяц"
          >
            <Icon name="chevronRight" size={18} />
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={goToday}>
            Сегодня
          </button>
        </div>

        <div className="chip-row toolbar__end">
          <button
            type="button"
            className={`chip ${scope === 'all' ? 'chip--active' : ''}`}
            onClick={() => setScope('all')}
          >
            Все
          </button>
          <button
            type="button"
            className={`chip ${scope === 'mine' ? 'chip--active' : ''}`}
            onClick={() => setScope('mine')}
          >
            Мои
          </button>
          <button
            type="button"
            className={`chip ${openOnly ? 'chip--active' : ''}`}
            onClick={() => setOpenOnly((v) => !v)}
            aria-pressed={openOnly}
          >
            Только незакрытые
          </button>
        </div>
      </div>

      {error && (
        <div className="state">
          <p className="state__title">{error}</p>
          <button type="button" className="btn btn--secondary" onClick={reload}>
            Повторить
          </button>
        </div>
      )}

      {!error && (
        <div className="cal-layout">
          <section className={`cal-grid ${loading ? 'cal-grid--loading' : ''}`}>
            <div className="cal-grid__weekdays">
              {WEEKDAY_LABELS.map((w) => (
                <span key={w} className="cal-grid__weekday">
                  {w}
                </span>
              ))}
            </div>

            <div className="cal-grid__body">
              {weeks.map((week) => (
                <div className="cal-grid__week" key={dayKey(week[0])}>
                  {week.map((date) => (
                    <DayCell
                      key={dayKey(date)}
                      date={date}
                      tasks={tasksOn(date)}
                      calls={callsOn(date)}
                      isToday={dayKey(date) === dayKey(today)}
                      isCurrentMonth={date.getMonth() === anchor.getMonth()}
                      isSelected={dayKey(date) === dayKey(selected)}
                      onSelect={setSelected}
                    />
                  ))}
                </div>
              ))}
            </div>

            <p className="cal-grid__footer text-muted">
              {loading ? 'Загрузка…' : `Задач со сроком в этом периоде: ${total}`}
            </p>
          </section>

          <aside className="cal-day">
            <header className="cal-day__head">
              <h2 className="cal-day__title">
                {selected.getDate()} {MONTH_LABELS[selected.getMonth()].toLowerCase()}
                {dayKey(selected) === dayKey(today) && (
                  <span className="badge badge--info">Сегодня</span>
                )}
              </h2>
              <span className="text-muted">
                {selectedTasks.length === 0 ? 'Задач нет' : `Задач: ${selectedTasks.length}`}
              </span>
            </header>

            {loading && (
              <div className="flex-col gap-sm">
                <span className="skeleton task-card__skeleton" />
                <span className="skeleton task-card__skeleton" />
              </div>
            )}

            {!loading && callsOn(selected).length > 0 && (
              <div className="flex-col gap-sm">
                <span className="text-muted video-hint">Видеозвонки</span>
                {callsOn(selected).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="cal-task"
                    onClick={() => navigate(`/admin/video/${c.id}`)}
                  >
                    <span className="cal-task__head">
                      <span className="cal-task__title">📹 {c.title}</span>
                    </span>
                    {c.description && (
                      <span className="cal-task__assignee text-muted">{c.description}</span>
                    )}
                    <span className="cal-task__tags">
                      <span className="badge">
                        {new Date(c.scheduled_at).toLocaleTimeString('ru-RU',
                          { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {!loading && selectedTasks.length === 0 && (
              <div className="state state--compact">
                <Icon name="calendar" size={28} />
                <p className="state__text">
                  На этот день ничего не запланировано.
                </p>
              </div>
            )}

            {!loading &&
              selectedTasks.map((t) => {
                const due = describeDue(t)
                const pr = priorityMeta(t.priority)
                return (
                  <button
                    key={t.id}
                    type="button"
                    className="cal-task"
                    onClick={() => navigate(`/admin/tasks/${t.id}`)}
                  >
                    <span className="cal-task__head">
                      <span className="cal-task__id">#{t.id}</span>
                      <span className="cal-task__title">{t.title}</span>
                    </span>
                    <span className="cal-task__tags">
                      <StatusBadge status={t.status} />
                      {t.priority !== 'normal' && (
                        <span className={`badge badge--${pr.tone}`}>{pr.label}</span>
                      )}
                      <DueBadge due={due} compact />
                    </span>
                    {t.assigned_to && (
                      <span className="cal-task__assignee text-muted">
                        {t.assigned_to.full_name}
                      </span>
                    )}
                  </button>
                )
              })}
          </aside>
        </div>
      )}
    </div>
  )
}
